import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FLOW_STAGE_TONE } from '../theme/adminVisualSystem';

/**
 * LevitatingStageMap — three floating "stage balls" with subtle physics.
 *
 *   - Each ball has a home position; gravity-style attraction returns it home.
 *   - Idle drift gives them a slow ambient float.
 *   - Soft inter-ball repulsion when they overlap → never collapse into each other.
 *   - Cursor proximity pulls a ball gently toward the pointer (and grows it).
 *   - Click → onSelect(stageKey) with a smooth scale-out flourish.
 *
 * No physics engine — a 60fps RAF loop with simple verlet-ish updates.
 *
 * Props:
 *   stages   — [{ key, label }]
 *   counts   — { ideas, in_review, todo }
 *   onSelect — (stageKey) => void
 */
const RADIUS_MIN = 110; // 220px diameter
const RADIUS_MAX = 180; // 360px diameter

function radiusForCount(count, total) {
  // Scale within [RADIUS_MIN, RADIUS_MAX]. Empty stages stay readable.
  if (!total || count <= 0) return RADIUS_MIN;
  const ratio = count / total;
  return RADIUS_MIN + ratio * (RADIUS_MAX - RADIUS_MIN);
}

function StageBall({ stage, count, x, y, r, hovered, onEnter, onLeave, onClick }) {
  const tone = FLOW_STAGE_TONE[stage.key];
  const size = r * 2;
  return (
    <motion.button
      type="button"
      data-clickable="true"
      data-testid={`stage-circle-${stage.key}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onClick}
      whileTap={{ scale: 0.96 }}
      className="absolute flex flex-col items-center justify-center text-center select-none"
      style={{
        left: x - r,
        top: y - r,
        width: size,
        height: size,
        borderRadius: '50%',
        border: `1px solid ${tone.border}`,
        background: '#0a0a0a',
        boxShadow: hovered
          ? `inset 0 0 36px ${tone.glow}, 0 0 28px ${tone.glow}`
          : `inset 0 0 24px ${tone.glow}, 0 0 14px ${tone.glow}`,
        transition: 'box-shadow 220ms ease',
      }}
    >
      {/* Slow rotating dotted ring */}
      <motion.span
        aria-hidden
        className="absolute inset-3 rounded-full pointer-events-none"
        style={{ border: `1px dashed ${tone.border}` }}
        animate={{ rotate: 360 }}
        transition={{ duration: 56, repeat: Infinity, ease: 'linear' }}
      />
      {/* Pulse halo */}
      <motion.span
        aria-hidden
        className="absolute inset-0 rounded-full pointer-events-none"
        animate={{
          boxShadow: [
            `0 0 0 0 ${tone.glow}`,
            `0 0 0 14px transparent`,
            `0 0 0 0 ${tone.glow}`,
          ],
        }}
        transition={{ duration: 3.2, repeat: Infinity }}
      />

      <p
        className="relative z-10 font-outfit text-[11px] sm:text-xs font-medium tracking-[0.28em] uppercase"
        style={{ color: tone.text }}
      >
        {stage.label}
      </p>
      <p className="relative z-10 font-outfit text-5xl sm:text-6xl font-bold text-white tabular-nums leading-none mt-1">
        {count}
      </p>
      <p className="relative z-10 text-[9px] font-mono text-[#777] tracking-[0.2em] uppercase mt-1">
        {count === 1 ? 'item' : 'items'}
      </p>
    </motion.button>
  );
}

export default function LevitatingStageMap({ stages, counts, onSelect }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 1000, h: 460 });

  // Ball state — refs so the RAF loop doesn't re-render every frame.
  const ballsRef = useRef(
    stages.map(() => ({
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      hx: 0,
      hy: 0, // home
      r: RADIUS_MIN,
      hovered: false,
    })),
  );

  const cursorRef = useRef({ x: -1e6, y: -1e6, active: false });
  const [tick, setTick] = useState(0); // forces re-render at ~30fps

  const total = counts.ideas + counts.in_review + counts.todo;

  // Update radii whenever counts change
  useEffect(() => {
    const balls = ballsRef.current;
    stages.forEach((s, i) => {
      const c = counts[s.key] || 0;
      balls[i].r = radiusForCount(c, total);
    });
  }, [stages, counts, total]);

  // Resize observer → recalculate home positions
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const recompute = () => {
      const rect = node.getBoundingClientRect();
      const w = rect.width;
      const h = Math.max(440, Math.min(560, w * 0.42));
      setSize({ w, h });
      // Distribute across width with slight vertical stagger for ambience
      const balls = ballsRef.current;
      const slots = [
        { x: w * 0.22, y: h * 0.55 },
        { x: w * 0.5, y: h * 0.42 },
        { x: w * 0.78, y: h * 0.55 },
      ];
      balls.forEach((b, i) => {
        b.hx = slots[i].x;
        b.hy = slots[i].y;
        if (b.x === 0 && b.y === 0) {
          b.x = b.hx;
          b.y = b.hy;
        }
      });
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Cursor tracking
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onMove = (e) => {
      const rect = node.getBoundingClientRect();
      cursorRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        active: true,
      };
    };
    const onLeave = () => {
      cursorRef.current.active = false;
      cursorRef.current.x = -1e6;
      cursorRef.current.y = -1e6;
    };
    node.addEventListener('mousemove', onMove);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      node.removeEventListener('mousemove', onMove);
      node.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // Physics loop
  useEffect(() => {
    let raf;
    let frame = 0;
    const step = () => {
      const balls = ballsRef.current;
      const { w, h } = size;
      const cur = cursorRef.current;
      // Each ball: ambient drift + spring to home + cursor pull + soft repulsion
      for (let i = 0; i < balls.length; i++) {
        const b = balls[i];
        // Ambient drift — sine waves keyed off ball index for staggered motion
        const t = frame / 60;
        const driftX = Math.sin(t * 0.45 + i * 1.1) * 0.32;
        const driftY = Math.cos(t * 0.38 + i * 0.7) * 0.28;
        b.vx += driftX * 0.05;
        b.vy += driftY * 0.05;

        // Spring back home
        const k = 0.012; // spring stiffness
        b.vx += (b.hx - b.x) * k;
        b.vy += (b.hy - b.y) * k;

        // Cursor pull when close
        if (cur.active) {
          const dx = cur.x - b.x;
          const dy = cur.y - b.y;
          const dist = Math.hypot(dx, dy);
          const pullR = b.r + 80;
          if (dist < pullR && dist > 0.5) {
            const intensity = (1 - dist / pullR) * 0.25;
            b.vx += (dx / dist) * intensity;
            b.vy += (dy / dist) * intensity;
          }
        }

        // Soft inter-ball repulsion
        for (let j = 0; j < balls.length; j++) {
          if (j === i) continue;
          const o = balls[j];
          const dx = b.x - o.x;
          const dy = b.y - o.y;
          const dist = Math.hypot(dx, dy);
          const minDist = b.r + o.r + 24;
          if (dist > 0.5 && dist < minDist) {
            const push = (minDist - dist) / minDist;
            b.vx += (dx / dist) * push * 0.7;
            b.vy += (dy / dist) * push * 0.7;
          }
        }

        // Damping
        b.vx *= 0.85;
        b.vy *= 0.85;
        // Clamp velocity so motion stays elegant
        const maxV = 2.4;
        b.vx = Math.max(-maxV, Math.min(maxV, b.vx));
        b.vy = Math.max(-maxV, Math.min(maxV, b.vy));

        // Integrate
        b.x += b.vx;
        b.y += b.vy;

        // Soft wall bounce
        if (b.x - b.r < 4) {
          b.x = b.r + 4;
          b.vx = Math.abs(b.vx) * 0.45;
        }
        if (b.x + b.r > w - 4) {
          b.x = w - b.r - 4;
          b.vx = -Math.abs(b.vx) * 0.45;
        }
        if (b.y - b.r < 4) {
          b.y = b.r + 4;
          b.vy = Math.abs(b.vy) * 0.45;
        }
        if (b.y + b.r > h - 4) {
          b.y = h - b.r - 4;
          b.vy = -Math.abs(b.vy) * 0.45;
        }
      }
      frame++;
      // Re-render at ~30fps to keep the DOM updates light
      if (frame % 2 === 0) setTick((n) => (n + 1) & 0xffff);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  const hoverBall = (i, h) => {
    ballsRef.current[i].hovered = h;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden border border-[#1a1a1a] bg-[#080808]"
      style={{ height: size.h }}
      data-testid="stages-map"
    >
      {/* Subtle dot grid for spatial feel */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '34px 34px',
        }}
      />

      {/* Balls (use tick to force re-render) */}
      <span hidden data-tick={tick} />
      {stages.map((s, i) => {
        const b = ballsRef.current[i];
        return (
          <StageBall
            key={s.key}
            stage={s}
            count={counts[s.key] || 0}
            x={b.x}
            y={b.y}
            r={b.r}
            hovered={b.hovered}
            onEnter={() => hoverBall(i, true)}
            onLeave={() => hoverBall(i, false)}
            onClick={() => onSelect(s.key)}
          />
        );
      })}

      <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[9px] font-mono text-[#444] tracking-[0.25em] uppercase">
        Click a stage to enter
      </p>
    </div>
  );
}
